import React, { useState, useCallback, useEffect, useMemo } from "react";
import { apiGet } from "../../../services/api";
import VisitList from "../VisitList";
import ChartHistoryPlayer from "../toothchart/ChartHistoryPlayer";
import ChartHistoryReplay from "./ChartHistoryReplay";
import ClinicalTimeline from "./timeline/ClinicalTimeline";
import type { ChartHistoryRecord } from "./timeline/ClinicalTimeline";
import { subscribeCareflowSync } from "../../../services/careflowSync";
import "./HistoryView.css";

export interface HistoryViewProps {
  patientId?: string | number | null;
  visitId?: string | number | null;
  isLocked?: boolean;
  refreshKey?: string | number;
}

export default function HistoryView({ patientId, visitId, isLocked, refreshKey }: HistoryViewProps) {
  const [selectedVisitId, setSelectedVisitId] = useState<string | number | null>(visitId != null ? visitId : null);
  const activeVisitId = selectedVisitId;
  const [timelineEvents, setTimelineEvents] = useState<ChartHistoryRecord[]>([]);
  const [observationNotes, setObservationNotes] = useState<string>("");
  const [treatmentSummary, setTreatmentSummary] = useState<Record<string, unknown> | null>(null);
  const [chartMode, setChartMode] = useState<"player" | "replay">("player");
  const [syncTick, setSyncTick] = useState(0);

  useEffect(() => {
    if (visitId != null) setSelectedVisitId(visitId);
  }, [visitId]);

  useEffect(() => {
    if (activeVisitId == null) {
      setTimelineEvents([]);
      setObservationNotes("");
      setTreatmentSummary(null);
      return;
    }
    const vid = String(activeVisitId);
    apiGet<ChartHistoryRecord[]>(`/api/doctor/chart-history/${vid}`)
      .then((res) => setTimelineEvents(Array.isArray(res) ? res : []))
      .catch(() => setTimelineEvents([]));
    apiGet<{ notes?: string }>(`/api/doctor/observation-notes/${vid}`)
      .then((res) => setObservationNotes(res?.notes || ""))
      .catch(() => setObservationNotes(""));
    apiGet<Record<string, unknown>>(`/api/doctor/treatment-summary/${vid}`)
      .then((res) => setTreatmentSummary(res ?? null))
      .catch(() => setTreatmentSummary(null));
  }, [activeVisitId, refreshKey, syncTick]);

  useEffect(() => {
    const unsubscribe = subscribeCareflowSync((payload) => {
      if (activeVisitId == null) return;
      const active = Number(activeVisitId);
      if (Number.isFinite(active) && payload.visitId != null && Number(payload.visitId) !== active) return;
      setSyncTick((prev) => prev + 1);
    });
    return () => unsubscribe();
  }, [activeVisitId]);

  const handleSelectVisit = useCallback((v: { visit_id?: string | number } | null) => {
    setSelectedVisitId(v?.visit_id ?? null);
  }, []);

  const visitIdStr = selectedVisitId != null ? String(selectedVisitId) : null;

  const emptyChartData = useMemo(
    () => ({ states: {}, surfaces: {}, flags: {}, movements: {} }),
    []
  );

  return (
    <div className="history-view">
      <div className="hv-visits-bar">
        <VisitList
          patientId={patientId ?? null}
          onSelectVisit={handleSelectVisit}
          activeVisitId={selectedVisitId}
        />
      </div>

      <div className="hv-center">
        <div className="hv-chart-section">
          <div className="hv-chart-header">
            <div className="hv-chart-title">Tooth Chart</div>
            <div className="hv-chart-actions">
              <button
                type="button"
                className={`hv-chart-btn ${chartMode === "player" ? "active" : ""}`}
                onClick={() => setChartMode("player")}
                disabled={!visitIdStr}
              >
                View
              </button>
              <button
                type="button"
                className={`hv-chart-btn ${chartMode === "replay" ? "active" : ""}`}
                onClick={() => setChartMode("replay")}
                disabled={!visitIdStr}
              >
                Replay
              </button>
            </div>
          </div>

          {chartMode === "replay" ? (
            <ChartHistoryReplay visitId={visitIdStr} />
          ) : (
            <ChartHistoryPlayer
              patientId={patientId != null ? String(patientId) : undefined}
              activeTooth={null}
              onToothClick={() => {}}
              isLocked={isLocked}
              onVisitSelect={() => {}}
              currentChartData={emptyChartData}
              selectedVisitId={visitIdStr}
              refreshKey={refreshKey}
            />
          )}
        </div>
      </div>

      <div className="hv-clinical-history">
        {(observationNotes || treatmentSummary) && (
          <div className="history-summary-card">
            {treatmentSummary && (
              <div className="summary-block">
                <h4>Treatment Summary</h4>
                <div className="summary-row">
                  <strong>Procedures:</strong>{" "}
                  {Array.isArray(treatmentSummary.procedures) && (treatmentSummary.procedures as string[]).length > 0
                    ? (treatmentSummary.procedures as string[]).join(", ")
                    : "—"}
                </div>
                <div className="summary-row">
                  <strong>Teeth:</strong>{" "}
                  {Array.isArray(treatmentSummary.teeth) && (treatmentSummary.teeth as string[]).length > 0
                    ? (treatmentSummary.teeth as string[]).join(", ")
                    : "—"}
                </div>
              </div>
            )}
            {observationNotes && (
              <div className="summary-block">
                <h4>Observation Notes</h4>
                <p>{observationNotes}</p>
              </div>
            )}
          </div>
        )}
        {timelineEvents.length === 0 ? (
          <div className="history-empty-inline">
            <p>No clinical history found for this visit.</p>
          </div>
        ) : (
          <ClinicalTimeline events={timelineEvents} />
        )}
      </div>
    </div>
  );
}
